import {
  acceptDraftPrices,
  draftPrices,
  draftStage,
  feedSaleStateFromAuctionStatus,
  fetchAuctionDrafts,
  withLiveFigures,
  DRAFT_STAGE_LABEL,
  type DraftStage,
  type LotDraft,
  type PriceKey,
  type PublicLotRow,
} from '@auctisync/api-client';
import { formatCents, formatInZone, resolveTimeZone, type Tables } from '@auctisync/shared';
import { Button } from '@auctisync/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  GRID_GROUP_LABEL,
  GRID_LOT_STATUSES,
  LOT_GRID_COLUMNS,
  REVIEWED_COLUMN,
  aiPriceEdits,
  cellChanged,
  cellText,
  centsToCell,
  clampColumnWidth,
  countEdits,
  editedPrices,
  liveReserveChanges,
  lockedReason,
  parseCell,
  parseStoredWidths,
  reviewedBy,
  reviewedCount,
  rowPatch,
  type GridColumn,
  type GridGroup,
  type LotRow,
  type RowEdits,
} from '../../lib/lotGrid';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useSaleFeed } from '../../hooks/useSaleFeed';
import { supabase } from '../../lib/supabase';

/**
 * The lot grid — every lot in a sale on one screen, every cell editable.
 *
 * Jim, 2026-10-06: "part of the auction admin build is supposed to be a
 * spreadsheet view of the lots. Going through lot page by lot page is too
 * slow… A key to this is going to be that the grid is a pop out page. This is
 * important because the user needs to be able to look at pictures and the
 * spreadsheet at the same time."
 *
 * So it is its OWN window: a route outside the admin chrome, opened with
 * `window.open` from the sale page, with no nav down the side. Put it on a
 * second monitor and keep the lot page, or the photos, on the first.
 *
 * WHAT IT IS NOT. It does not upload photos, run AI research or review a
 * draft's suggestions — those stay on the lot page, where there is room to
 * look at them. This is for typing.
 *
 * HOW A SAVE WORKS. Edits queue in `edits` (lot id → column → value) and
 * nothing is written until Save. Then, lot by lot:
 *   1. a lot whose AI draft is still waiting on its prices, and whose prices
 *      the person touched, goes through `acceptDraftPrices` — the one door
 *      that checks staff, refuses a live lot, refuses a reserve under the
 *      starting bid, and stamps the draft reviewed so it stops asking;
 *   2. then the row's patch is written to `lots`.
 * A lot the server refuses keeps its edits and turns red with the reason; the
 * rest still save. Half-saving one LOT is not possible — its prices and its
 * other fields go in that order, and the first failure stops that row.
 *
 * 🔴 The rules — which columns exist, what a cell means, what a lot with bids
 * will not let you change — are `lib/lotGrid.ts`, and tested there.
 */

type Category = Pick<Tables<'item_categories'>, 'id' | 'name'>;
type Consignor = Pick<Tables<'consignors'>, 'id' | 'display_name'>;
type Profile = Pick<Tables<'consignment_profiles'>, 'id' | 'name'>;
type Scale = Pick<Tables<'bid_increment_scales'>, 'id' | 'name'>;
type Photo = { lot_id: string | null; display_url: string | null; sort_order: number | null };

const VISIBLE_KEY = 'auctisync.lotGrid.columns';
const WIDTH_KEY = 'auctisync.lotGrid.widths';

/**
 * Column widths the person has dragged, remembered in this browser. A grid is
 * somebody's own layout: having to widen Title again every time you open it
 * would make the drag pointless.
 */
function useColumnWidths(): [
  Record<string, number>,
  (key: string, px: number) => void,
  (key: string) => void,
] {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = window.localStorage.getItem(WIDTH_KEY);
      return raw ? parseStoredWidths(JSON.parse(raw) as unknown) : {};
    } catch {
      return {};
    }
  });
  const remember = (next: Record<string, number>) => {
    setWidths(next);
    try {
      window.localStorage.setItem(WIDTH_KEY, JSON.stringify(next));
    } catch {
      /* the layout just does not outlive the window */
    }
  };
  return [
    widths,
    (key, px) => {
      remember({ ...widths, [key]: clampColumnWidth(px) });
    },
    (key) => {
      const next = { ...widths };
      delete next[key];
      remember(next);
    },
  ];
}

/** The columns on screen — the person's own choice, remembered in this browser. */
function useVisibleColumns(): [Set<string>, (key: string, on: boolean) => void, () => void] {
  const fallback = useMemo(
    () => new Set(LOT_GRID_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)),
    [],
  );
  const [visible, setVisible] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(VISIBLE_KEY);
      if (!raw) return fallback;
      const keys = JSON.parse(raw) as unknown;
      if (!Array.isArray(keys)) return fallback;
      const known = keys.filter((k): k is string => typeof k === 'string');
      return known.length > 0 ? new Set(known) : fallback;
    } catch {
      // A browser that refuses storage still gets a grid.
      return fallback;
    }
  });
  const remember = (next: Set<string>) => {
    setVisible(next);
    try {
      window.localStorage.setItem(VISIBLE_KEY, JSON.stringify([...next]));
    } catch {
      /* the choice just does not outlive the window */
    }
  };
  return [
    visible,
    (key, on) => {
      const next = new Set(visible);
      if (on) next.add(key);
      else next.delete(key);
      remember(next);
    },
    () => {
      remember(new Set(fallback));
    },
  ];
}

export function AdminLotGrid() {
  const { id: auctionId } = useParams();
  const [auction, setAuction] = useState<Pick<
    Tables<'auctions'>,
    'id' | 'title' | 'status' | 'timezone'
  > | null>(null);
  const [lots, setLots] = useState<LotRow[]>([]);
  const [drafts, setDrafts] = useState<Map<string, LotDraft>>(new Map());
  const [photos, setPhotos] = useState<Map<string, string>>(new Map());
  const [categories, setCategories] = useState<Category[]>([]);
  const [consignors, setConsignors] = useState<Consignor[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [scales, setScales] = useState<Scale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [edits, setEdits] = useState<Map<string, RowEdits>>(new Map());
  const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map());
  const [cellError, setCellError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ lotId: string; url: string } | null>(null);
  const [visible, toggleColumn, resetColumns] = useVisibleColumns();
  const [widths, setWidth, clearWidth] = useColumnWidths();
  const [showColumns, setShowColumns] = useState(false);
  const widthOf = useCallback((c: GridColumn) => widths[c.key] ?? c.width, [widths]);

  const columns = useMemo(() => LOT_GRID_COLUMNS.filter((c) => visible.has(c.key)), [visible]);

  const load = useCallback(async () => {
    if (!auctionId) return;
    setLoading(true);
    setError(null);
    const { data: a, error: aErr } = await supabase
      .from('auctions')
      .select('id, title, status, timezone')
      .eq('id', auctionId)
      .maybeSingle();
    if (aErr || !a) {
      setError(aErr?.message ?? 'That sale could not be found.');
      setLoading(false);
      return;
    }
    setAuction(a);
    // `lots_staff`: the office's own view — it carries the columns the shared
    // roles on `lots` cannot read (added_in_warehouse_at, the boxes).
    const { data, error: lErr } = await supabase
      .from('lots_staff')
      .select('*')
      .eq('auction_id', auctionId)
      .order('lot_number', { ascending: true });
    if (lErr) {
      setError(lErr.message);
      setLoading(false);
      return;
    }
    setLots((data ?? []) as LotRow[]);

    const [cats, cons, profs, scal, pics] = await Promise.all([
      supabase.from('item_categories').select('id, name').order('name'),
      supabase.from('consignors').select('id, display_name').order('display_name'),
      supabase.from('consignment_profiles').select('id, name').order('name'),
      supabase.from('bid_increment_scales').select('id, name').order('name'),
      supabase
        .from('inventory_photos')
        .select('lot_id, display_url, sort_order')
        .in(
          'lot_id',
          (data ?? []).map((l) => l.id),
        )
        .order('sort_order', { ascending: true }),
    ]);
    setCategories(cats.data ?? []);
    setConsignors(cons.data ?? []);
    setProfiles(profs.data ?? []);
    setScales(scal.data ?? []);
    const first = new Map<string, string>();
    for (const p of (pics.data ?? []) as Photo[]) {
      if (p.lot_id && p.display_url && !first.has(p.lot_id)) first.set(p.lot_id, p.display_url);
    }
    setPhotos(first);
    try {
      const rows = await fetchAuctionDrafts(supabase, auctionId);
      setDrafts(new Map(rows.map((d) => [d.lot_id, d])));
    } catch {
      // The AI column is a guide, not the list.
      setDrafts(new Map());
    }
    setLoading(false);
  }, [auctionId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 🔴 THE LIVE COLUMNS ARE LIVE (2026-10-08). Bids, current bid, close time
   * and status come from the house's staff feed on a sale the engine runs —
   * every lot, every change — or from Realtime on the Supabase book. It
   * matters beyond the numbers: the cells a lot with bids LOCKS
   * (`lockedReason`) unlock or lock by the live count, not one loaded minutes
   * ago. Only what is DRAWN is overlaid; edits are kept and saved against the
   * rows as loaded, so a live figure never becomes a write.
   */
  const { source } = useLiveSource(lots[0]?.id ?? null);
  const live = useSaleFeed(source, {
    summary: true,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const onRealtimeRow = useCallback((row: PublicLotRow) => {
    setLots((prev) =>
      prev.map((l) =>
        l.id === row.id
          ? {
              ...l,
              current_bid_cents: row.current_bid_cents,
              bid_count: row.bid_count,
              ends_at: row.ends_at,
              status: row.status,
            }
          : l,
      ),
    );
  }, []);
  useLotSubscription({
    target: { kind: 'auction', auctionId: auctionId ?? '' },
    onUpdate: onRealtimeRow,
    enabled: source?.kind === 'supabase' && auctionId !== undefined,
  });
  const liveFeed = live.feed;
  const shownLots: LotRow[] = useMemo(
    () =>
      lots.map((l) => {
        const view = liveFeed?.lot(l.id);
        if (view === undefined) return l;
        const f = withLiveFigures(l, view);
        // The house's own feed hides nothing from it: a null count is "none yet".
        return { ...f, bid_count: f.bid_count ?? 0 };
      }),
    // `live.version` re-reads the feed.
    [lots, liveFeed, live.version],
  );

  const { cells: editedCells, lots: editedLots } = countEdits(edits);

  // A window with unsaved typing in it should not close quietly.
  useEffect(() => {
    if (editedCells === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [editedCells]);

  const setCell = useCallback((lot: LotRow, column: GridColumn, raw: string) => {
    const parsed = parseCell(column, raw);
    if (!parsed.ok) {
      setCellError(`Lot ${lot.lot_number}, ${column.label}: ${parsed.message}`);
      return false;
    }
    setCellError(null);
    setEdits((prev) => {
      const next = new Map(prev);
      const row = { ...(next.get(lot.id) ?? {}) };
      if (cellChanged(lot, column, parsed.value)) row[column.key] = parsed.value;
      else delete row[column.key];
      if (Object.keys(row).length === 0) next.delete(lot.id);
      else next.set(lot.id, row);
      return next;
    });
    return true;
  }, []);

  /** What a cell holds right now: the pending edit if there is one, else the lot. */
  const valueOf = useCallback(
    (lot: LotRow, column: GridColumn): string => {
      const pending = edits.get(lot.id)?.[column.key];
      if (pending === undefined) return cellText(lot, column);
      if (pending === null) return '';
      // Money reads as money even while it is unsaved — "12.50", not "12.5".
      if (column.kind === 'money') return centsToCell(Number(pending));
      return String(pending);
    },
    [edits],
  );

  const boolOf = useCallback(
    (lot: LotRow, column: GridColumn): boolean => {
      const pending = edits.get(lot.id)?.[column.key];
      if (pending !== undefined) return Boolean(pending);
      return Boolean(lot[(column.target as { column: keyof LotRow }).column]);
    },
    [edits],
  );

  /**
   * Stage the AI's suggested prices on every lot still waiting for them — the
   * whole point of the grid for a freshly catalogued sale (Jim's 10-lot test
   * sale had nine lots at "Prices to review", each needing its own page).
   * Nothing is written: they land as pending edits to eyeball, then Save.
   */
  const lotsAwaitingPrices = useMemo(
    () => lots.filter((l) => draftStage(drafts.get(l.id) ?? null) === 'prices_to_review'),
    [lots, drafts],
  );

  const fillAiPrices = useCallback(() => {
    setEdits((prev) => {
      const next = new Map(prev);
      for (const lot of lotsAwaitingPrices) {
        const row = aiPriceEdits(lot, draftPrices(drafts.get(lot.id) ?? null));
        if (Object.keys(row).length === 0) continue;
        next.set(lot.id, { ...(next.get(lot.id) ?? {}), ...row });
      }
      return next;
    });
  }, [lotsAwaitingPrices, drafts]);

  async function save() {
    if (editedCells === 0) return;
    // "Open for bidding" is the LIVE status.
    const openReserves = liveReserveChanges(shownLots, edits);
    if (
      openReserves > 0 &&
      !window.confirm(
        openReserves === 1
          ? 'One lot that is open for bidding will have its reserve changed. Bidders will see straight away whether the new reserve is met. Continue?'
          : `${openReserves} lots that are open for bidding will have their reserves changed. Bidders will see straight away whether the new reserves are met. Continue?`,
      )
    ) {
      return;
    }
    setSaving(true);
    setCellError(null);
    const failures = new Map<string, string>();
    const saved: string[] = [];

    for (const lot of lots) {
      const row = edits.get(lot.id);
      if (!row || Object.keys(row).length === 0) continue;

      // A draft still waiting on its prices goes through the shared door, which
      // stamps it reviewed — so the grid clears "Prices to review" too.
      const pending = draftStage(drafts.get(lot.id) ?? null) === 'prices_to_review';
      const touched = editedPrices(row);
      if (pending && touched.length > 0) {
        const overrides: Partial<Record<PriceKey, number>> = {};
        for (const [key, price] of [
          ['msrp_cents', 'msrp_cents'],
          ['retail_price_cents', 'retail_price_cents'],
          ['starting_bid_cents', 'starting_bid_cents'],
          ['reserve_cents', 'reserve_cents'],
          ['estimate_min_cents', 'estimate_min_cents'],
          ['estimate_max_cents', 'estimate_max_cents'],
        ] as [string, PriceKey][]) {
          const edited = row[key];
          const cents = edited === undefined ? lot[key as keyof LotRow] : edited;
          if (typeof cents === 'number') overrides[price] = cents;
        }
        const res = await acceptDraftPrices(supabase, lot.id, overrides);
        if (!res.ok) {
          failures.set(lot.id, res.message);
          continue;
        }
      }

      const { error: err } = await supabase
        .from('lots')
        .update(rowPatch(lot, row))
        .eq('id', lot.id);
      if (err) failures.set(lot.id, err.message);
      else saved.push(lot.id);
    }

    setEdits((prev) => {
      const next = new Map(prev);
      for (const id of saved) next.delete(id);
      return next;
    });
    setRowErrors(failures);
    setSaving(false);
    await load();
  }

  if (loading) {
    return (
      <div data-theme="admin" style={padStyle}>
        Loading the lots…
      </div>
    );
  }
  if (error) {
    return (
      <div data-theme="admin" style={{ ...padStyle, color: 'var(--semantic-error)' }}>
        {error}
      </div>
    );
  }

  const label = (column: GridColumn, lot: LotRow): string => {
    const key = column.key;
    if (key === 'category_id') return categories.find((c) => c.id === lot.category_id)?.name ?? '';
    if (key === 'consignor_id') {
      return consignors.find((c) => c.id === lot.consignor_id)?.display_name ?? '';
    }
    return '';
  };

  const options = (column: GridColumn): { value: string; label: string }[] => {
    switch (column.key) {
      case 'status':
        return GRID_LOT_STATUSES.map((s) => ({ value: s, label: s }));
      case 'category_id':
        return [
          { value: '', label: '—' },
          ...categories.map((c) => ({ value: c.id, label: c.name })),
        ];
      case 'consignor_id':
        return [
          { value: '', label: '—' },
          ...consignors.map((c) => ({ value: c.id, label: c.display_name })),
        ];
      case 'consignment_profile_id':
        return [
          { value: '', label: '—' },
          ...profiles.map((p) => ({ value: p.id, label: p.name })),
        ];
      case 'bid_increment_scale_id':
        return [{ value: '', label: '—' }, ...scales.map((s) => ({ value: s.id, label: s.name }))];
      default:
        return [];
    }
  };

  const readOnlyText = (lot: LotRow, column: GridColumn): string => {
    switch (column.key) {
      case 'current_bid_cents':
        return lot.current_bid_cents === null ? '—' : formatCents(lot.current_bid_cents);
      case 'ends_at':
        // The SALE's local time, zone named — never the viewer's (memory:
        // feedback_times_are_the_auctions_local_time).
        return lot.ends_at ? formatInZone(lot.ends_at, resolveTimeZone(auction?.timezone)) : '—';
      case 'added_in_warehouse_at':
        return lot.added_in_warehouse_at ? 'yes' : '';
      default:
        return cellText(lot, column);
    }
  };

  return (
    /* 🔴 `data-theme="admin"` is what defines --surface, --text and the rest.
       Outside the chrome layout nothing else sets it, and every token resolved
       to nothing: the frozen columns came out transparent and the scrolling
       ones painted straight through them. Measured, not guessed. */
    <div style={shellStyle} data-theme="admin" data-testid="lot-grid">
      <LiveStatusBanner status={live.status} timeZone={auction?.timezone} audience="staff" />
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)' }}>
            {auction?.title ?? 'Lots'}
          </h1>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {lots.length} lot{lots.length === 1 ? '' : 's'} · {reviewedCount(lots)} reviewed · click
            a cell and type · Tab and the arrow keys move · Esc puts a cell back
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {editedCells === 0
              ? 'No unsaved changes'
              : `${editedCells} change${editedCells === 1 ? '' : 's'} on ${editedLots} lot${
                  editedLots === 1 ? '' : 's'
                }`}
          </span>
          {lotsAwaitingPrices.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={fillAiPrices}
              data-testid="lot-grid-fill-ai"
              title="Put what the AI suggested into the price cells. Nothing is saved until you press Save."
            >
              Fill in AI prices ({lotsAwaitingPrices.length})
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowColumns((v) => !v)}
            data-testid="lot-grid-columns"
          >
            Columns
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={editedCells === 0 || saving}
            onClick={() => {
              setEdits(new Map());
              setRowErrors(new Map());
              setCellError(null);
            }}
          >
            Discard
          </Button>
          <Button
            type="button"
            disabled={editedCells === 0 || saving}
            onClick={() => void save()}
            data-testid="lot-grid-save"
          >
            {saving ? 'Saving…' : `Save ${editedCells || ''}`.trim()}
          </Button>
        </div>
      </header>

      {showColumns && (
        <div style={columnsPanelStyle} data-testid="lot-grid-columns-panel">
          <div style={columnsPanelHeadStyle}>
            <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
              Which columns to show
            </strong>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button type="button" style={linkButtonStyle} onClick={resetColumns}>
                Back to the usual columns
              </button>
              {/* Jim, 2026-10-06: "a clear minimize button … so you can collapse
                  it when you are done." */}
              <Button
                type="button"
                variant="outline"
                data-testid="lot-grid-columns-done"
                onClick={() => setShowColumns(false)}
              >
                ▲ Done
              </Button>
            </div>
          </div>
          {(Object.keys(GRID_GROUP_LABEL) as GridGroup[]).map((group) => (
            <div key={group} style={{ minWidth: '12rem' }}>
              <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {GRID_GROUP_LABEL[group]}
              </strong>
              {LOT_GRID_COLUMNS.filter((c) => c.group === group).map((c) => (
                <label
                  key={c.key}
                  style={{
                    display: 'flex',
                    gap: '0.4rem',
                    fontSize: '0.85rem',
                    padding: '0.1rem 0',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={visible.has(c.key)}
                    onChange={(e) => toggleColumn(c.key, e.target.checked)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      {cellError && (
        <div role="alert" style={alertStyle}>
          {cellError}
        </div>
      )}
      {rowErrors.size > 0 && (
        <div role="alert" style={alertStyle}>
          {rowErrors.size === 1 ? '1 lot was not saved' : `${rowErrors.size} lots were not saved`} —
          the reason is on the row, and the changes are still there.
        </div>
      )}

      <div style={scrollStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {/* Jim, 2026-10-06: Reviewed is the FAR LEFT column, and pinned
                  there with the photo — the office reads it from anywhere in
                  the sale, however far right it has scrolled. */}
              <th style={{ ...thStyle, width: REVIEWED_COLUMN.width, left: 0, zIndex: 4 }}>
                {REVIEWED_COLUMN.label}
              </th>
              <th style={{ ...thStyle, width: 52, left: REVIEWED_COLUMN.width, zIndex: 4 }}>
                Photo
              </th>
              <th style={{ ...thStyle, width: 130, left: REVIEWED_COLUMN.width + 52, zIndex: 4 }}>
                AI draft
              </th>
              {columns.map((c) => {
                const w = widthOf(c);
                return (
                  <th
                    key={c.key}
                    style={{
                      ...thStyle,
                      width: w,
                      minWidth: w,
                      maxWidth: w,
                      position: 'sticky',
                      top: 0,
                    }}
                    title={c.label}
                  >
                    <span style={clipStyle}>{c.label}</span>
                    <ColumnGrip
                      label={c.label}
                      width={w}
                      onResize={(px) => setWidth(c.key, px)}
                      onReset={() => clearWidth(c.key)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shownLots.map((lot) => {
              const failed = rowErrors.get(lot.id);
              const stage = draftStage(drafts.get(lot.id) ?? null);
              const url = photos.get(lot.id);
              // 🔴 A frozen column must be OPAQUE: `transparent` lets the
              // columns scrolling underneath show through it.
              const rowBg = failed
                ? 'var(--surface-danger, #fef2f2)'
                : edits.has(lot.id)
                  ? 'var(--surface-elev)'
                  : 'var(--surface)';
              return (
                <tr
                  key={lot.id}
                  data-testid={`lot-grid-row-${lot.lot_number}`}
                  style={{ background: rowBg }}
                >
                  <td
                    style={{
                      ...tdStyle,
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      background: rowBg,
                      width: REVIEWED_COLUMN.width,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input
                        type="checkbox"
                        data-testid={`lot-grid-reviewed-${lot.lot_number}`}
                        aria-label={`Reviewed, lot ${lot.lot_number}`}
                        checked={boolOf(lot, REVIEWED_COLUMN)}
                        onChange={(e) => {
                          setCell(lot, REVIEWED_COLUMN, String(e.target.checked));
                        }}
                      />
                      {/* The initials are the SERVER's — a tick not saved yet
                          has none, and they appear once it is. */}
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)',
                          minWidth: '1.6rem',
                        }}
                        title={
                          lot.reviewed_at
                            ? `Reviewed ${new Date(lot.reviewed_at).toLocaleString()}`
                            : undefined
                        }
                      >
                        {reviewedBy(lot)}
                      </span>
                    </span>
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      position: 'sticky',
                      left: REVIEWED_COLUMN.width,
                      zIndex: 2,
                      background: rowBg,
                    }}
                  >
                    {url ? (
                      <button
                        type="button"
                        title="Look at this lot’s photo"
                        onClick={() => setPreview({ lotId: lot.id, url })}
                        style={thumbButtonStyle}
                      >
                        <img src={url} alt="" style={thumbStyle} />
                      </button>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                    )}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      position: 'sticky',
                      left: REVIEWED_COLUMN.width + 52,
                      zIndex: 2,
                      background: rowBg,
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                    }}
                    title={failed ?? undefined}
                  >
                    <span style={clipStyle}>
                      {stage === 'not_sent' ? '' : DRAFT_STAGE_LABEL[stage as DraftStage]}
                    </span>
                    {failed ? (
                      <span
                        style={{
                          ...clipStyle,
                          color: 'var(--semantic-error)',
                          marginTop: '0.2rem',
                        }}
                      >
                        {failed}
                      </span>
                    ) : null}
                  </td>
                  {columns.map((c) => (
                    <Cell
                      key={c.key}
                      lot={lot}
                      column={c}
                      width={widthOf(c)}
                      edited={edits.get(lot.id)?.[c.key] !== undefined}
                      text={valueOf(lot, c)}
                      checked={boolOf(lot, c)}
                      display={label(c, lot)}
                      options={options(c)}
                      readOnlyText={readOnlyText(lot, c)}
                      onCommit={(raw) => setCell(lot, c, raw)}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {preview && (
        <aside style={previewStyle}>
          <img src={preview.url} alt="" style={{ maxWidth: '100%', borderRadius: '0.4rem' }} />
          <Button type="button" variant="outline" onClick={() => setPreview(null)}>
            Close
          </Button>
        </aside>
      )}
    </div>
  );
}

/**
 * The handle on a column's right edge (Jim, 2026-10-06: "make the column
 * widths drag-editable"). Drag it to resize, double-click to put the column
 * back to its usual width, or use the arrow keys when a pointer is not how
 * someone works.
 *
 * The drag is tracked on the WINDOW, not the handle: a pointer moving faster
 * than React re-renders leaves the handle behind, and the column would stop
 * following halfway through the drag.
 *
 * 🔴 IT HAS TO LOOK DRAGGABLE. It shipped invisible, and Jim could not tell
 * the columns resized at all: "add visible bars so the user can see where to
 * click". So every column edge carries a rule, which thickens and takes the
 * house colour under the pointer and while dragging.
 */
function ColumnGrip({
  label,
  width,
  onResize,
  onReset,
}: {
  label: string;
  width: number;
  onResize: (px: number) => void;
  onReset: () => void;
}) {
  const [hot, setHot] = useState(false);
  const [dragging, setDragging] = useState(false);

  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = width;
    setDragging(true);
    const move = (ev: PointerEvent) => {
      onResize(startWidth + (ev.clientX - startX));
    };
    const end = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    // Without these the drag selects the page's text as it goes.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Width of ${label}`}
      tabIndex={0}
      data-testid="lot-grid-grip"
      title="Drag to resize · double-click to reset"
      onPointerDown={start}
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onResize(width - 16);
        else if (e.key === 'ArrowRight') onResize(width + 16);
        else return;
        e.preventDefault();
      }}
      style={{
        ...gripStyle,
        // The bar itself: a quiet rule down every edge, the house colour and
        // thicker once the pointer is on it or a drag is under way.
        // Measured on screen, not assumed: `--border` is #e5e5e5 and
        // `--border-strong` #d4d4d4 — both are row hairlines, and at 2px
        // neither reads as "grab me here". The resting bar is therefore mixed
        // from the muted ink, which lands around #a9a9a9 on white.
        borderRightWidth: hot || dragging ? 3 : 2,
        borderRightColor:
          hot || dragging
            ? 'var(--brand-primary, #2264dc)'
            : 'color-mix(in oklab, var(--text-muted, #525252) 55%, transparent)',
      }}
    />
  );
}

/**
 * One cell. It holds its own text while being typed in and reports upward on
 * Enter, Tab or leaving — so a half-typed amount is never parsed, and Esc puts
 * the cell back to what it held.
 */
function Cell({
  lot,
  column,
  width,
  text,
  checked,
  display,
  options,
  readOnlyText,
  edited,
  onCommit,
}: {
  lot: LotRow;
  column: GridColumn;
  width: number;
  text: string;
  checked: boolean;
  display: string;
  options: { value: string; label: string }[];
  readOnlyText: string;
  edited: boolean;
  onCommit: (raw: string) => boolean;
}) {
  const locked = lockedReason(lot, column);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const input = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(text);
  }, [text, editing]);

  const cell: React.CSSProperties = {
    ...tdStyle,
    width,
    maxWidth: width,
    background: edited ? 'var(--brand-primary-subtle, #eff4ff)' : undefined,
    cursor: locked ? 'not-allowed' : 'text',
    color: locked ? 'var(--text-muted)' : 'var(--text)',
  };

  if (column.kind === 'readonly') {
    return (
      <td style={{ ...cell, color: 'var(--text-muted)' }} title={readOnlyText || undefined}>
        <span style={clipStyle}>{readOnlyText}</span>
      </td>
    );
  }

  if (column.kind === 'bool') {
    return (
      <td style={cell}>
        <input
          type="checkbox"
          aria-label={`${column.label}, lot ${lot.lot_number}`}
          checked={checked}
          disabled={Boolean(locked)}
          onChange={(e) => onCommit(String(e.target.checked))}
        />
      </td>
    );
  }

  if (column.kind === 'select') {
    return (
      <td style={cell}>
        <select
          aria-label={`${column.label}, lot ${lot.lot_number}`}
          value={text}
          disabled={Boolean(locked)}
          title={locked ?? undefined}
          onChange={(e) => onCommit(e.target.value)}
          style={fieldStyle}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
    );
  }

  if (locked) {
    return (
      <td style={cell} title={locked}>
        <span style={clipStyle}>{display || text}</span>
      </td>
    );
  }

  const commit = () => {
    setEditing(false);
    if (draft !== text && !onCommit(draft)) setDraft(text);
  };

  if (!editing) {
    return (
      <td
        style={cell}
        tabIndex={0}
        role="button"
        aria-label={`${column.label}, lot ${lot.lot_number}`}
        onFocus={() => setEditing(true)}
        onClick={() => setEditing(true)}
        title={display || text || undefined}
      >
        <span style={clipStyle}>
          {display || text || <span style={{ color: 'var(--text-muted)' }}>—</span>}
        </span>
      </td>
    );
  }

  const Tag = column.kind === 'longtext' ? 'textarea' : 'input';
  return (
    <td style={cell}>
      <Tag
        ref={input as never}
        autoFocus
        aria-label={`${column.label}, lot ${lot.lot_number}`}
        value={draft}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            setDraft(text);
            setEditing(false);
          } else if (e.key === 'Enter' && column.kind !== 'longtext') {
            e.preventDefault();
            commit();
          }
        }}
        style={{ ...fieldStyle, minHeight: column.kind === 'longtext' ? '4rem' : undefined }}
      />
    </td>
  );
}

const shellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: 'var(--surface)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap',
  padding: '0.6rem 1rem',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface-elev)',
  position: 'sticky',
  top: 0,
  zIndex: 5,
};

const columnsPanelHeadStyle: React.CSSProperties = {
  flexBasis: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

const columnsPanelStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1.5rem',
  flexWrap: 'wrap',
  padding: '0.75rem 1rem',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface-elev)',
};

const scrollStyle: React.CSSProperties = { flex: 1, overflow: 'auto' };

const tableStyle: React.CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontSize: '0.85rem',
  whiteSpace: 'nowrap',
  // 🔴 Fixed, and every cell clips (`clipStyle`). Left to itself a table sizes
  // columns to their content, so one long title made the columns jump as you
  // typed — and the overflow painted straight over the frozen Photo and AI
  // columns.
  tableLayout: 'fixed',
  width: 'max-content',
};

/** Keep a value inside its own column, with an ellipsis when it will not fit. */
const clipStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const gripStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  // 🔴 ENTIRELY INSIDE ITS OWN COLUMN. It first straddled the edge so it could
  // be grabbed from either side — and the bar vanished: each header cell is
  // sticky with its own z-index, so the NEXT column painted over the half that
  // crossed into it. A child cannot escape its parent's stacking context, so
  // the fix is to not cross at all.
  right: 0,
  width: 10,
  height: '100%',
  cursor: 'col-resize',
  borderRightStyle: 'solid',
  boxSizing: 'border-box',
  touchAction: 'none',
};

const thStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  background: 'var(--surface-elev)',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  fontWeight: 600,
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  background: 'inherit',
  // Every cell is opaque: the frozen columns sit over the scrolling ones.
  backgroundClip: 'padding-box',
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.2rem 0.35rem',
  border: '1px solid var(--border)',
  borderRadius: '0.25rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  font: 'inherit',
};

const thumbButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'none',
  padding: 0,
  cursor: 'zoom-in',
};

const thumbStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  objectFit: 'cover',
  borderRadius: '0.25rem',
};

const previewStyle: React.CSSProperties = {
  position: 'fixed',
  right: '1rem',
  bottom: '1rem',
  width: 'min(26rem, 40vw)',
  padding: '0.75rem',
  borderRadius: 'var(--r-card, 0.5rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface-elev)',
  boxShadow: 'var(--elev-2, 0 10px 30px rgba(0,0,0,0.2))',
  display: 'grid',
  gap: '0.5rem',
  zIndex: 10,
};

const alertStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  borderBottom: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
};

const linkButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'none',
  color: 'var(--brand-primary-text, var(--brand-primary))',
  cursor: 'pointer',
  fontSize: '0.8rem',
  alignSelf: 'flex-end',
};

const padStyle: React.CSSProperties = { padding: '2rem', color: 'var(--text-muted)' };
